'use client';

import { createContext, useContext, ReactNode, useState, useCallback } from 'react';
import { ResolvedVisualSettings } from 'utils/pageHelpers/visualSettings';
import { CustomQueryStringParameterItems } from 'libs/contentful/types';
import { EnrollmentUrlContext } from 'utils/linkHelpers';
import { PageContextValue } from './pageContextValue';

interface PageContextType {
  page?: PageContextValue;
  visualSettings?: ResolvedVisualSettings;
  enrollmentUrlContext?: EnrollmentUrlContext;
  updateVisualSettings?: (settings: Partial<ResolvedVisualSettings>) => void;
  /** Client preview override for custom QSPs; when set, used instead of server-resolved/baseline */
  resolvedCustomQueryStringParametersOverride?: CustomQueryStringParameterItems[] | null;
  updateResolvedCustomQueryStringParameters?: (items: CustomQueryStringParameterItems[] | null) => void;
}

const PageContext = createContext<PageContextType>({});

/**
 * Context provider for page-level data.
 * Provides a centralized way to access page-level data within page components.
 *
 * @example
 * <PageProvider page={toPageContextValue(page)} visualSettings={visualSettings}>
 *   <Component />
 * </PageProvider>
 */
export const PageProvider = ({
  children,
  page,
  visualSettings: initialVisualSettings,
  enrollmentUrlContext,
}: {
  children: ReactNode;
  page?: PageContextValue;
  visualSettings?: ResolvedVisualSettings;
  enrollmentUrlContext?: EnrollmentUrlContext;
}) => {
  const [visualSettings, setVisualSettings] = useState<ResolvedVisualSettings | undefined>(initialVisualSettings);
  const [resolvedCustomQueryStringParametersOverride, setResolvedCustomQueryStringParametersOverride] = useState<
    CustomQueryStringParameterItems[] | null | undefined
  >(undefined);

  const updateVisualSettings = useCallback((settings: Partial<ResolvedVisualSettings>) => {
    setVisualSettings(prev => {
      if (!prev) {
        return undefined;
      }
      return { ...prev, ...settings };
    });
  }, []);

  const updateResolvedCustomQueryStringParameters = useCallback((items: CustomQueryStringParameterItems[] | null) => {
    setResolvedCustomQueryStringParametersOverride(items);
  }, []);

  return (
    <PageContext.Provider
      value={{
        page,
        visualSettings,
        enrollmentUrlContext,
        updateVisualSettings,
        resolvedCustomQueryStringParametersOverride,
        updateResolvedCustomQueryStringParameters,
      }}>
      {children}
    </PageContext.Provider>
  );
};

/**
 * Hook to access page-level data.
 *
 * @example
 * const Component = () => {
 *   const { page } = usePage();
 *   return <div>{page?.customQueryStringParametersCollection?.items?.length}</div>;
 * }
 */
export const usePage = () => {
  const context = useContext(PageContext);
  return context;
};
